import { ReactElement, VFC } from 'react';
import { useRouter } from 'next/router';

import cn from 'classnames';
import truncate from 'lodash.truncate';

// import { TableSortDirection } from '@/types/table.d';

import { Button } from 'ui';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import AssetLogo from '@/components/global/AssetLogo';

import { Structure } from '@/types/app';

import pFormatter from '@/util/helpers/pFormatter';
import nFormatter from '@/util/helpers/nFormatter';

import { FaCaretUp, FaCaretDown } from 'react-icons/fa';

/**
 * A reusable Table Row component
 * @component
 */

const DeltaArrow = ({ dir }: { dir: boolean }) => (dir ? <FaCaretUp /> : <FaCaretDown />);

const TableRow: VFC<{
  instrument?: Structure.AssetInfo;
  url?: string;
}> = ({ instrument, url }): ReactElement => {
  const { t } = useTranslation('detail');
  const { push } = useRouter();

  if (!instrument)
    return (
      <tr className="animate-pulse">
        {/* Logo and Name */}
        <td className="w-full py-3 pr-0 pl-1 mobile:pl-3 desktop:rounded-l-lg">
          <div className="flex items-center">
            <div className="hidden h-8 w-8 shrink-0 overflow-hidden rounded-full border border-gray-6 tablet:block">
              <AssetLogo width={40} height={40} />
            </div>

            <div className="flex h-9 max-w-43 shrink-0 grow items-center rounded-lg tablet:ml-2">
              <div className="flex flex-col">
                <div className="h-9 w-42 rounded-md bg-gray-7" />
              </div>
            </div>
          </div>
        </td>
        {/* Price */}
        <td className="py-3 px-0 text-right tablet:table-cell">
          <div className="mobile:mx-3">
            <div className="h-6 w-14 rounded-md bg-gray-7" />
          </div>
        </td>
        {/* 24h */}
        <td className="hidden py-3 px-0 text-right tablet:table-cell">
          <div className="mx-1 ml-[9px] flex items-center justify-center">
            <div className="h-6 w-14 rounded-md bg-gray-7" />
          </div>
        </td>
        {/* 7D % */}
        <td className="hidden py-3 px-0 text-center tablet-table-1:table-cell">
          <div className="mr-3 ml-2 flex items-center justify-center">
            <div className="h-6 w-13 rounded-md bg-gray-7" />
          </div>
        </td>
        {/* Vol 24hr */}
        <td className="hidden py-3 text-center desktop:table-cell">
          <div className="mx-2 ml-[13px] flex items-center justify-center">
            <div className="h-6 w-13 rounded-md bg-gray-7" />
          </div>
        </td>
        {/* Mkt Cap */}
        <td className="hidden py-3 text-center tablet-table-2:table-cell">
          <div className="mx-3 flex items-center justify-center">
            <div className="h-6 w-11 rounded-md bg-gray-7" />
          </div>
        </td>
        {/* 7 Day Chart */}
        <td className="hidden py-3 text-center desktop-table-1:table-cell">
          <div className="mx-3 flex items-center justify-center">
            <div className="h-[50.5px] w-19 rounded-md bg-gray-7" />
          </div>
        </td>
        {/* Transact */}
        <td className="hidden py-3 pl-0 text-center tablet:table-cell tablet:pr-0 desktop:rounded-r-lg desktop:pr-2">
          <div className="ml-3 flex w-35  shrink-0 grow items-center justify-between gap-5 rounded-lg">
            <div className="h-6 w-12 rounded-md bg-gray-7" />
            <div className="h-9 w-[88px] rounded-lg bg-gray-7" />
          </div>
        </td>
      </tr>
    );

  const { changePercent24H, changePercent7D, price, volume24H, marketCap, code, name, type } =
    instrument;

  return (
    <tr
      onClick={() => {
        if (url) push(url);
      }}
      className={cn({ 'cursor-pointer transition-colors tablet-table-1:hover:bg-gray-6': url })}
    >
      {/* Logo and Name */}
      <td className="w-full py-3 pr-0 pl-1 mobile:pl-3 desktop:rounded-l-lg">
        <div className="flex items-center">
          <div className="hidden h-8 w-8 shrink-0 overflow-hidden rounded-full border border-gray-6 tablet:block">
            <AssetLogo width={40} height={40} alt="Company Logo" type={type} assetName={name} />
          </div>

          <div className="flex h-9 max-w-43 shrink-0 grow items-center rounded-lg tablet:ml-2">
            <div className="flex flex-col">
              <div className="max-w-42 font-medium">
                {truncate(name, {
                  length: 45,
                })}
              </div>
              <div className="text-gray-4 text-body-3">{code}</div>
            </div>
          </div>
        </div>
      </td>
      {/* Price */}
      <td className="py-3 px-0 text-right tablet:table-cell">
        <div className="mobile:mx-3">${pFormatter(price)}</div>
      </td>
      {/* 24h */}
      <td className="hidden py-3 px-0 text-right tablet:table-cell">
        <div className="mx-3 flex items-center">
          {Number(changePercent24H?.toFixed(2)) !== 0 && (
            <DeltaArrow dir={Math.sign(Number(changePercent24H?.toFixed(2))) === 1} />
          )}
          {changePercent24H?.toFixed(2)}%
        </div>
      </td>
      {/* 7D % */}
      <td className="hidden py-3 px-0 text-center tablet-table-1:table-cell">
        <div className="mx-3 flex items-center">
          {Number(changePercent7D?.toFixed(2)) !== 0 && (
            <DeltaArrow dir={Math.sign(Number(changePercent7D?.toFixed(2))) === 1} />
          )}
          {changePercent7D?.toFixed(2)}%
        </div>
      </td>
      {/* Vol 24hr */}
      <td className="hidden py-3 text-center desktop:table-cell">
        <div className="mx-3">${nFormatter(volume24H, 1)}</div>
      </td>
      {/* Mkt Cap */}
      <td className="hidden py-3 text-center tablet-table-2:table-cell">
        <div className="mx-3">${nFormatter(marketCap, 1)}</div>
      </td>
      {/* 7 Day Chart */}
      <td className="hidden py-3 text-center desktop-table-1:table-cell">
        <div className="mx-3 w-19">
          <Image
            height={44}
            alt={`${code} line chart.`}
            width={94}
            priority
            loader={() => `/api/data/generateSparkline?symbol=${code}`}
            src="chart.png"
          />
        </div>
      </td>
      {/* Transact */}
      <td className="hidden py-3 pl-0 text-center tablet:table-cell tablet:pr-0 desktop:rounded-r-lg desktop:pr-2">
        <div className="ml-3 flex w-35  shrink-0 grow items-center justify-between gap-5 rounded-lg">
          <Button className="h-9 w-fit !min-w-0 !p-0 font-normal !text-body-2 hover:!text-blue-3">
            {instrument.type === Structure.PagedResponseAssetInfoType.CRYPTO
              ? t('operations.crypto_secondary')
              : t('operations.stock_secondary')}
          </Button>
          <Button
            className="h-9 !min-w-[88px] !p-0 !text-body-2 hover:border-black hover:bg-transparent hover:text-black focus:border-black focus:bg-transparent focus:text-black active:border-black active:bg-transparent active:text-black"
            variant="primary"
            size="small"
          >
            {t('operations.primary')}
          </Button>
        </div>
      </td>
    </tr>
  );
};

export default TableRow;
